// NcoGridFooter.tsx
/**
 * Simplified footer for NCO grid - shows selection count only.
 * Update functionality is handled via inline editing (useInlineMassUpdate).
 */
import { Box, Chip, Typography } from '@mui/material'
import { GridFooterContainer, GridPagination, GridRowSelectionModel, useGridApiContext } from '@mui/x-data-grid-premium'
import { FloatingActionBar } from '../shared'
import * as React from 'react'

interface NcoGridFooterProps {
  rowSelectionModel: GridRowSelectionModel
  serverSideMode?: boolean
}

const NcoGridFooter: React.FC<NcoGridFooterProps> = ({
  rowSelectionModel,
  serverSideMode = true,
}) => {
  const apiRef = useGridApiContext()

  // Selection count (supports Set-based or array-based models)
  const selectionCount = rowSelectionModel?.ids?.size ?? 0
  const hasSelection = selectionCount > 0

  const close = React.useCallback(() => {
    try {
      // Robust clear for both shapes
      const ids = (rowSelectionModel as any)?.ids
      if (ids && typeof ids.forEach === 'function') {
        ids.forEach((id: any) => apiRef.current.selectRow(id, false))
      }
      // DataGrid expects an array for selection model
      apiRef.current.setRowSelectionModel({ ids: new Set(), type: 'include' })
    } catch {
      // no-op: keep this safe across versions
    }
  }, [apiRef, rowSelectionModel])

  const displayLabel = selectionCount === 1 ? 'deal' : 'deals'

  return (
    <GridFooterContainer
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 52,
        overflow: 'hidden',
        px: 2,
      }}
    >
      {hasSelection ? (
        <FloatingActionBar
          visible={true}
          onClose={close}
          styles={{
            width: 'auto',
          }}
          sx={{
            backgroundColor: 'background.section',
            backdropFilter: 'blur(10px)',
            flexWrap: 'nowrap',
            overflow: 'visible',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 0.5,
            }}
          >
            {/* Selection count */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={selectionCount}
                sx={{ color: 'rgb(24, 144, 255)', fontSize: 16, fontWeight: 'bold' }}
              />
              <Typography variant="body2" noWrap>
                {displayLabel} selected
              </Typography>
            </Box>
          </Box>
        </FloatingActionBar>
      ) : (
        <>
          {/* Left side spacer */}
          <Box />

          {/* Right side: Pagination */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {serverSideMode && <GridPagination />}
          </Box>
        </>
      )}
    </GridFooterContainer>
  )
}

export default React.memo(NcoGridFooter)
